import { get } from "utils/lodash";
import { formatPrice } from "utils/currencyFormatter";

export interface SlotDiscount {
  shopId: string;
  /** "-15%" hoặc "Giá mới 399.000đ" */
  discountLabel: string;
  /** % giảm (0 nếu là new_price) */
  discountPct: number;
  /** "09:00 – 12:00" theo giờ Việt Nam */
  timeRange: string;
  /** "T2–T6" | "Cả tuần" | "T7, CN" ... */
  days: string;
  dayCodes: string[];
  endDate?: string;
}

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABEL: Record<string, string> = {
  mon: "T2",
  tue: "T3",
  wed: "T4",
  thu: "T5",
  fri: "T6",
  sat: "T7",
  sun: "CN",
};

/**
 * BE lưu startTime/endTime dạng 1970-01-01THH:mm:00.000Z (UTC).
 * Đổi sang giờ VN (+7) và format HH:mm.
 */
export const slotTimeVN = (iso?: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const mins = (d.getUTCHours() * 60 + d.getUTCMinutes() + 7 * 60) % (24 * 60);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** Rút gọn danh sách ngày: T2–T6 / Cả tuần / T7, CN */
export const formatDays = (codes: string[] = [], allLabel = "Cả tuần"): string => {
  const sorted = DAY_ORDER.filter((d) => codes.includes(d));
  if (!sorted.length) return "";
  if (sorted.length === 7) return allLabel;
  // Liên tiếp?
  const idx = sorted.map((d) => DAY_ORDER.indexOf(d));
  const consecutive = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  if (consecutive && sorted.length >= 3) {
    return `${DAY_LABEL[sorted[0]]}–${DAY_LABEL[sorted[sorted.length - 1]]}`;
  }
  return sorted.map((d) => DAY_LABEL[d]).join(", ");
};

/** Chuẩn hoá 1 campaignPackage type=slot thành SlotDiscount (null nếu hết hạn / không hợp lệ) */
export const toSlotDiscount = (pkg: any, newPriceLabel = "Giá mới"): SlotDiscount | null => {
  if (!pkg || get(pkg, "type") !== "slot") return null;
  if (get(pkg, "isExpired") || get(pkg, "promotionStatus") !== "active") return null;
  const shopId = get(pkg, "shop.id") || (typeof pkg.shop === "string" ? pkg.shop : "");
  if (!shopId) return null;
  const start = slotTimeVN(get(pkg, "startTime"));
  const end = slotTimeVN(get(pkg, "endTime"));
  if (!start || !end || start === end) return null;
  const type = get(pkg, "discountType");
  const value = Number(get(pkg, "discountValue")) || 0;
  let discountLabel = "";
  let discountPct = 0;
  if (type === "percentage" && value > 0) {
    discountPct = Math.round(value);
    discountLabel = `-${discountPct}%`;
  } else if (type === "new_price" && value > 0) {
    discountLabel = `${newPriceLabel} ${formatPrice(value)}`;
  } else return null;
  const dayCodes: string[] = get(pkg, "dayOfWeek", []) || [];
  return {
    shopId,
    discountLabel,
    discountPct,
    timeRange: `${start} – ${end}`,
    days: formatDays(dayCodes),
    dayCodes,
    endDate: get(pkg, "endDate"),
  };
};

/** Map shopId → deal tốt nhất (ưu tiên % cao nhất) */
export const slotDiscountByShop = (pkgs: any[] = []): Record<string, SlotDiscount> => {
  const map: Record<string, SlotDiscount> = {};
  for (const p of pkgs) {
    const deal = toSlotDiscount(p);
    if (!deal) continue;
    const cur = map[deal.shopId];
    if (!cur || deal.discountPct > cur.discountPct) map[deal.shopId] = deal;
  }
  return map;
};

/** % giảm so với giá menu (0 nếu không tính được) */
export const offerDiscountPct = (pkg: any): number => {
  const offerPrice = Number(get(pkg, "displayDiscountPrice")) || 0;
  const originalPrice = Number(get(pkg, "service.menuPrice")) || 0;
  return originalPrice && offerPrice && originalPrice > offerPrice
    ? Math.round(((originalPrice - offerPrice) / originalPrice) * 100)
    : 0;
};

